import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AgentTaskStatus,
  MigrationDeployInput,
  TaskStatusAndRequestResponseTuple
} from '~/protos/agents'
import { submitDeployInput } from '~/services/taskService'
import { BetterToast } from '~/utils/toast'
import {
  AzureDeployModal,
  type AzureDeployPhase,
  type DeployInputRequest
} from './AzureDeployModal'

const VALID_PHASES: AzureDeployPhase[] = [
  'needs_connection',
  'deploying',
  'awaiting_input',
  'done',
  'failed'
]

/** Workspace-scoped integrations settings deep-link for the "Connect Azure" CTA. */
const connectAzureRoute = (workspaceUuid: string) => `/w/${workspaceUuid}/settings?tab=integrations`

/** Newest-first scan for the most recent deploy state emitted on the stream. */
const findLatestDeployState = (tuples: TaskStatusAndRequestResponseTuple[]) => {
  for (let i = tuples.length - 1; i >= 0; i -= 1) {
    const state = tuples[i]?.response?.[0]?.responseContentStream?.migrationDeployState
    if (state) return state
  }
  return undefined
}

export interface AzureDeployTaskModalProps {
  tuples: TaskStatusAndRequestResponseTuple[]
  /** Index of the tuple this modal was opened from (unused today; kept for parity). */
  index: number
  taskId: number
  workspaceUuid: string
  uuid: string
  /** Current top-level task status — drives whether input can be submitted. */
  currentTaskStatus: AgentTaskStatus | null
}

/**
 * Task-side container for the Azure deploy modal. Derives the presentational
 * `AzureDeployModal` props from the latest `MigrationDeployState` on the task's
 * response stream, and submits the user's answer via `submitDeployInput`
 * during the MIGRATION_AWAITING_DEPLOY_INPUT pause.
 */
export const AzureDeployTaskModal = ({
  tuples,
  taskId,
  workspaceUuid,
  uuid,
  currentTaskStatus
}: AzureDeployTaskModalProps) => {
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const deployState = useMemo(() => findLatestDeployState(tuples), [tuples])

  const awaiting = currentTaskStatus === AgentTaskStatus.MIGRATION_AWAITING_DEPLOY_INPUT

  // Prefer the phase the agent reported; fall back to deriving from status.
  const phase: AzureDeployPhase = useMemo(() => {
    const reported = deployState?.phase
    if (reported && VALID_PHASES.includes(reported as AzureDeployPhase)) {
      return reported as AzureDeployPhase
    }
    return awaiting ? 'awaiting_input' : 'deploying'
  }, [deployState?.phase, awaiting])

  const inputRequest: DeployInputRequest | undefined = useMemo(() => {
    const req = deployState?.inputRequest
    if (phase !== 'awaiting_input' || !req) return undefined
    return {
      id: req.id,
      kind: req.kind === 'confirm' ? 'confirm' : 'text',
      label: req.label,
      placeholder: req.placeholder || undefined,
      defaultValue: req.defaultValue || undefined,
      isSecret: req.isSecret,
      confirmLabel: req.confirmLabel || undefined,
      denyLabel: req.denyLabel || undefined
    }
  }, [deployState?.inputRequest, phase])

  const submit = useCallback(
    async (deployInput: MigrationDeployInput) => {
      if (!awaiting) {
        BetterToast.error('Deploy is no longer waiting for input.')
        return
      }
      setIsSubmitting(true)
      try {
        await submitDeployInput({ workspaceUuid, uuid, taskId, deployInput })
      } catch (error) {
        BetterToast.error(error instanceof Error ? error.message : 'Failed to submit deploy input')
      } finally {
        setIsSubmitting(false)
      }
    },
    [awaiting, workspaceUuid, uuid, taskId]
  )

  const onSubmitText = useCallback(
    (value: string) =>
      submit(MigrationDeployInput.fromPartial({ requestId: inputRequest?.id ?? '', value })),
    [submit, inputRequest?.id]
  )

  const onConfirm = useCallback(
    (approved: boolean) =>
      submit(MigrationDeployInput.fromPartial({ requestId: inputRequest?.id ?? '', approved })),
    [submit, inputRequest?.id]
  )

  return (
    <AzureDeployModal
      isOpen
      phase={phase}
      log={deployState?.log ?? ''}
      inputRequest={inputRequest}
      onSubmitText={onSubmitText}
      onConfirm={onConfirm}
      isSubmitting={isSubmitting}
      triggerUrl={deployState?.triggerUrl || undefined}
      onConnect={() => navigate(connectAzureRoute(workspaceUuid))}
    />
  )
}

export default AzureDeployTaskModal
